import React from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import logoDlb from "../../img/MCL png.png";
import { formatCellValue } from "./ExcelPdfFormateCellValue";

const STATUS_DISPLAY_MAP = {
  INVALID: "UNDESIGNATED",
  COMPLETED: "DESIGNATED",
};

// 🔹 Helper: Convert image file URL → Base64
const getBase64FromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

class ExportPdf extends React.Component {
  // Helper function to extract nested values safely

  // Add this method to your ExportPdf component (the second one)

  generateTreeFlowPDF = (
    doc,
    data,
    headers,
    nestedHeadersConfig,
    startY,
    hierarchyList,
  ) => {
    let currentY = startY;

    const hierarchy =
      hierarchyList.length > 0
        ? hierarchyList
        : [
            { key: "mines", label: "Mines", color: [15, 93, 63] },
            {
              key: "siteCategory",
              label: "Site Categories",
              color: [139, 46, 15],
            },
            { key: "siteInfoDTOs", label: "Sites", color: [90, 46, 166] },
          ];

    const mainColor = [31, 42, 68];

    const getHeaders = (level) => {
      if (level === 0) return headers.filter((h) => h?.isShow !== false);
      const hierarchyKey = hierarchy[level - 1]?.key;
      return (nestedHeadersConfig[hierarchyKey] || []).filter(
        (h) => h?.isShow !== false,
      );
    };

    // Helper to format cell value
    const formatCellValue = (row, header, index) => {
      if (header.sNo) {
        return (index + 1).toString();
      }
      if (header.isActive && header.keyName === "isActive") {
        return row[header.keyName] ? "Active" : "InActive";
      }
      if (header.keyName === "status") {
        return row.isActive ? "Active" : "InActive";
      }
      const value = row[header.keyName];
      if (value === undefined || value === null) return "";

      if (header?.keyName === "tripStatus" || header?.isBox) {
        return this.mapStatusValue(value);
      }

      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (header.isDate && value) {
        return moment(value).format("DD-MM-YYYY HH:mm:ss");
      }
      return value;
    };

    // 🔹 Single Row Table
    const drawRow = (row, headersList, level, index, color) => {
      const headerNames = headersList.map((h) => h.name || h.keyName || "");
      const bodyRow = headersList.map((header, colIndex) => {
        let value = formatCellValue(row, header, index);
        // indent first column for nested levels
        if (colIndex === 1 && level > 0) {
          value = "   ".repeat(level) + value;
        }
        return value;
      });

      doc.autoTable({
        startY: currentY,
        head: [headerNames],
        body: [bodyRow],
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 4,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: color,
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fillColor: level === 0 ? [255, 255, 255] : [250, 250, 250],
        },
        margin: { left: level === 0 ? 40 : 40 + level * 10 },
      });

      currentY = doc.lastAutoTable.finalY + 4;
    };

    // 🔁 RECURSIVE TREE FLOW
    const render = (rows, level = 0) => {
      if (!rows || !rows.length) return;

      const headersList = getHeaders(level);
      const color =
        level === 0
          ? mainColor
          : hierarchy[level - 1]?.color || [100, 100, 100];

      rows.forEach((row, index) => {
        // Check if we need a new page
        if (currentY > doc.internal.pageSize.getHeight() - 100) {
          doc.addPage();
          currentY = 40;

          // Add title on new page
          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.text(
            this.props.title || "Report",
            doc.internal.pageSize.getWidth() / 2,
            30,
            { align: "center" },
          );

          // Add logo on new page
          this.addLogoToDoc(doc);
        }

        // 🔹 Draw current row
        drawRow(row, headersList, level, index, color);

        // 🔁 Go deeper
        const next = hierarchy[level];
        if (next && row[next.key]?.length) {
          render(row[next.key], level + 1);
        }
      });
    };

    render(data, 0);

    return currentY;
  };

  // Add this helper method to add logo
  addLogoToDoc = async (doc) => {
    try {
      const logoBase64 = await getBase64FromUrl(logoDlb);
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.addImage(logoBase64, "PNG", pageWidth - 100, 15, 60, 30);
    } catch (err) {
      console.warn("Logo load failed:", err);
    }
  };
  getNestedValue = (obj, path, defaultValue = "") => {
    try {
      const value = path
        .split(".")
        .reduce((acc, part) => acc && acc[part], obj);
      return value || defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };

  getDuration = (startTime, endTime) => {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    const diffMs = endDate - startDate; // difference in milliseconds

    const seconds = Math.floor((diffMs / 1000) % 60);
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Format location for display
  formatLocation = (row) => {
    if (row.address) return row.address;
    if (row.latitude && row.longitude) {
      return `${row.latitude.toFixed(6)}, ${row.longitude.toFixed(6)}`;
    }
    return "N/A";
  };

  mapStatusValue = (value) => {
    if (typeof value === "string") {
      const upper = value.trim().toUpperCase();
      return STATUS_DISPLAY_MAP[upper] || value;
    }
    return value;
  };

  generateUptimeMonthlyPDF = async (
    doc,
    rows,
    startY,
    pageWidth,
    pageHeight,
  ) => {
    let currentY = startY;

    // Remove header row from data and separate main data from control room data
    const headerRow = rows[0]; // First row is header
    const mainDataRows = rows.slice(1, -1); // Middle rows are main data
    const controlRoomRow = rows[rows.length - 1]; // Last row is control room

    // 🔹 FIRST TABLE: Main Data Table
    const mainHeaders = [
      "Area Name",
      "Date From",
      "Date To",
      "Total GPS Device",
      "Monthly Uptime%",
      "Monthly Downtime%",
    ];

    const mainTableData = mainDataRows.map((row) => [
      row.col1 || "",
      row.col2 || "",
      row.col3 || "",
      row.col4 || "",
      row.col5 || "",
      row.col6 || "",
    ]);

    // Generate main table
    doc.autoTable({
      startY: currentY,
      head: [mainHeaders],
      body: mainTableData,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 4,
        valign: "middle",
        halign: "center",
      },
      headStyles: {
        fillColor: [0, 58, 107],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    currentY = doc.lastAutoTable.finalY + 5; // Reduced space from 20 to 5

    // 🔹 SECOND TABLE: Control Room Equipment Table
    // const controlRoomHeaders = [
    //   "Date From",
    //   "Date To",
    //   "Total Uptime%",
    //   "Downtime%",
    // ];

    // const controlRoomData = [
    //   [
    //     // controlRoomRow.col1 || "",
    //     controlRoomRow.col2 || "",
    //     controlRoomRow.col3 || "",
    //     controlRoomRow.col5 || "",
    //     // controlRoomRow.col4 || "", // Empty column
    //     controlRoomRow.col6 || "",
    //   ],
    // ];

    // Generate control room table directly without text label
    // doc.autoTable({
    //   startY: currentY,
    //   head: [controlRoomHeaders],
    //   body: controlRoomData,
    //   theme: "grid",
    //   styles: {
    //     fontSize: 8,
    //     cellPadding: 4,
    //     valign: "middle",
    //     halign: "center",
    //   },
    //   headStyles: {
    //     fillColor: [70, 130, 180], // Different color for control room header
    //     textColor: 255,
    //     fontStyle: "bold",
    //     halign: "center",
    //   },
    //   bodyStyles: {
    //     fontStyle: "bold",
    //   },
    // });

    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
      pageWidth - 60,
      pageHeight - 20,
    );
  };

  // Add this new method to handle vehicle interaction notes
  generateVehicleInteractionNotesPDF = async (
    doc,
    rows,
    startY,
    pageWidth,
    pageHeight,
  ) => {
    let currentY = startY;

    // Outer table headers for vehicle summary
    const outerHeaders = ["S. NO", "VEHICLE NUMBER", "TOTAL REMARKS"];

    // Inner table headers for remark details
    const innerHeaders = [
      "S. No",
      "Vehicle Name	",
      "Notes On Vehicle	",
      "Create Time	",
      "Reason",
      "Notes Created By	",
    ];

    const outerTableWidth = pageWidth - 80;
    const innerTableWidth = pageWidth - 80;

    // Generate content for each vehicle one by one
    for (let i = 0; i < rows.length; i++) {
      const vehicle = rows[i];
      const remarks = vehicle.vehicleRemarksDTOS || [];

      // Check if we need a new page before adding outer table row
      if (currentY > pageHeight - 150) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Outer Table Row for this vehicle
      const outerTableData = [
        [
          (i + 1).toString(),
          vehicle.vehicleNumber || "N/A",

          remarks.length.toString(),
        ],
      ];

      // Generate single row outer table
      doc.autoTable({
        startY: currentY,
        head: [outerHeaders],
        body: outerTableData,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 6,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: [0, 58, 107],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: 0,
          fontStyle: "bold",
        },
        margin: { top: 5, bottom: 0 },
        tableWidth: outerTableWidth,
      });

      currentY = doc.lastAutoTable.finalY + 2;

      // Check if we need a new page before adding inner table
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Inner Table for this vehicle's remarks
      if (remarks.length > 0) {
        // Prepare inner table data
        const innerTableData = remarks.map((remark, remarkIndex) => [
          (remarkIndex + 1).toString(),
          remark.vehicleName || "NA",
          remark.remark || "N/A",
          remark.createdAt
            ? moment(remark.createdAt).format("DD-MM-YYYY HH:mm:ss")
            : "NA",
          this.getNestedValue(remark, "eventId.name", "NA"),
          remark.createdByName || "NA",
        ]);

        // Generate inner table
        doc.autoTable({
          startY: currentY,
          head: [innerHeaders],
          body: innerTableData,
          theme: "grid",
          styles: {
            fontSize: 7,
            cellPadding: 3,
            valign: "middle",
            halign: "left",
          },
          headStyles: {
            fillColor: [70, 130, 180],
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          tableWidth: innerTableWidth,
          pageBreak: "auto",
          didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
              `Page ${
                doc.internal.getCurrentPageInfo().pageNumber
              } of ${pageCount}`,
              pageWidth - 60,
              pageHeight - 20,
            );
          },
        });

        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        // No remarks for this vehicle
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("No remarks found for this vehicle", 50, currentY);
        currentY += 20;
      }

      // Add separation between vehicles (except for the last one)
      if (i < rows.length - 1) {
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(40, currentY, pageWidth - 40, currentY);
        currentY += 10;
      }
    }
  };

  // Add this new method after generateVehicleInteractionNotesPDF method
  generateGroupByTransporterPDF = async (
    doc,
    outerTableData,
    outerHeaders,
    innerHeaders,
    startY,
    pageWidth,
    pageHeight,
  ) => {
    let currentY = startY;

    // Prepare outer headers (for transporter summary)
    const formattedOuterHeaders = outerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Prepare inner headers (for trip details)
    const formattedInnerHeaders = innerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    // Generate content for each transporter one by one
    for (let i = 0; i < outerTableData.length; i++) {
      const transporter = outerTableData[i];
      const trips = transporter.alerts || [];

      // Check if we need a new page before adding outer table row
      if (currentY > pageHeight - 150) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Outer Table Row for this transporter
      const outerTableRow = [
        [
          transporter.sNo || (i + 1).toString(),
          transporter.vendorName || "N/A",
          transporter.totalTrips?.toString() || "0",
        ],
      ];

      // Generate single row outer table for transporter
      doc.autoTable({
        startY: currentY,
        head: [formattedOuterHeaders],
        body: outerTableRow,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 6,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: [0, 58, 107], // Navy Blue
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fillColor: [240, 248, 255], // Light blue background for transporter row
          textColor: 0,
          fontStyle: "bold",
        },
        margin: { top: 5, bottom: 2 },
      });

      // Set currentY directly after outer table with minimal gap
      currentY = doc.lastAutoTable.finalY + 5;

      // Check if we need a new page before adding inner table
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Inner Table for this transporter's trips
      if (trips.length > 0) {
        // Prepare inner table data for trips
        const innerTableData = trips.map((trip, tripIndex) => {
          return formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";

            let cellValue = formatCellValue(trip, headerConfig, tripIndex);

            if (headerConfig?.keyName === "tripStatus" || headerConfig?.isBox) {
              cellValue = this.mapStatusValue(cellValue);
            }

            return cellValue;
          });
        });

        // Generate inner table for trips
        doc.autoTable({
          startY: currentY,
          head: [formattedInnerHeaders],
          body: innerTableData,
          theme: "grid",
          styles: {
            fontSize: 7,
            cellPadding: 3,
            valign: "middle",
            halign: "left",
          },
          headStyles: {
            fillColor: [70, 130, 180], // Steel Blue for inner table
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          pageBreak: "auto",
          didDrawPage: (data) => {
            // Footer with page numbers
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
              `Page ${
                doc.internal.getCurrentPageInfo().pageNumber
              } of ${pageCount}`,
              pageWidth - 60,
              pageHeight - 20,
            );
          },
        });

        currentY = doc.lastAutoTable.finalY + 20;
      } else {
        // No trips for this transporter
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("No trips found for this transporter", 50, currentY);
        currentY += 25;
      }

      // Add separation between transporters (except for the last one)
      if (i < outerTableData.length - 1) {
        // Add a separator line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(40, currentY, pageWidth - 40, currentY);
        currentY += 15;
      }
    }

    // Add final page numbers if no tables were generated
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
      pageWidth - 60,
      pageHeight - 20,
    );
  };

  generateNitReportPDF = async (
    doc,
    outerTableData,
    outerHeaders,
    innerHeaders,
    startY,
    pageWidth,
    pageHeight,
  ) => {
    let currentY = startY;

    const formattedOuterHeaders = outerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    const formattedInnerHeaders = innerHeaders
      .filter((h) => h.isExportShow !== false)
      .map((h) => h.name || h.keyName || "");

    for (let i = 0; i < outerTableData.length; i++) {
      const nitRecord = outerTableData[i];
      const distanceRanges = nitRecord.alerts || [];

      // Check if we need a new page before adding outer table row
      if (currentY > pageHeight - 150) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Outer Table Row for this NIT record
      // 🔹 Outer Table Row for this NIT record
      const outerTableRow = [
        [
          nitRecord.sNo || (i + 1).toString(),
          nitRecord.mineName || "N/A",
          nitRecord.nitNo || "N/A",
          nitRecord.contractorName || "N/A",
          nitRecord.totalAllowedTippers?.toString() || "0",
          nitRecord.totalTippersWithGps?.toString() || "0",
          nitRecord.totalVehiclesWithoutGps?.toString() || "0",
          nitRecord.noOfValidTripsDiesel?.toString() || "0",
          nitRecord.totalDistanceOfValidTrips?.toString() || "0",
          nitRecord.noOfInvalidTrips?.toString() || "0",
          nitRecord.totalDistanceOfInvalidTrips?.toString() || "0",
        ],
      ];
      doc.autoTable({
        startY: currentY,
        head: [formattedOuterHeaders],
        body: outerTableRow,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 6,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: [0, 58, 107],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fillColor: [240, 248, 255],
          textColor: 0,
          fontStyle: "bold",
        },
        margin: { top: 5, bottom: 2 },
      });

      currentY = doc.lastAutoTable.finalY + 10;

      // Check if we need a new page before adding inner table
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Inner Table for this NIT's distance ranges
      if (distanceRanges.length > 0) {
        const innerTableData = distanceRanges.map((range, rangeIndex) => {
          return formattedInnerHeaders.map((header, idx) => {
            const headerConfig = innerHeaders[idx];
            if (!headerConfig) return "";

            // Handle S.No column
            if (headerConfig.sNo) {
              return (rangeIndex + 1).toString();
            }

            // Handle the range column
            if (headerConfig.keyName === "range") {
              return range.range || "N/A";
            }

            // Handle other columns - map from the range object properties
            switch (headerConfig.keyName) {
              case "validTripCount":
                return range.validTripCount?.toString() || "0";
              case "validTotalDistance":
                return range.validTotalDistance?.toString() || "0";
              case "noOfInvalidTrips":
                return range.noOfInvalidTrips?.toString() || "0";
              case "totalDistanceOfInvalidTrips":
                return range.totalDistanceOfInvalidTrips?.toString() || "0";
              case "inValidtripCount":
                return range.inValidtripCount?.toString() || "0";
              case "inValidtotalDistance":
                return range.inValidtotalDistance?.toString() || "0";
              default:
                return (
                  this.getNestedValue(range, headerConfig.keyName) || "N/A"
                );
            }
          });
        });

        doc.autoTable({
          startY: currentY,
          head: [formattedInnerHeaders],
          body: innerTableData,
          theme: "grid",
          styles: {
            fontSize: 8,
            cellPadding: 4,
            valign: "middle",
            halign: "center", // Center align for better readability
          },
          headStyles: {
            fillColor: [70, 130, 180],
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          pageBreak: "auto",
          didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
              `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
              pageWidth - 60,
              pageHeight - 20,
            );
          },
        });

        currentY = doc.lastAutoTable.finalY + 20;
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("No distance ranges found for this NIT", 50, currentY);
        currentY += 25;
      }

      // Add separation between NIT records (except for the last one)
      if (i < outerTableData.length - 1) {
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(40, currentY, pageWidth - 40, currentY);
        currentY += 15;
      }
    }

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
      pageWidth - 60,
      pageHeight - 20,
    );
  };

  // expandTransporterHeaders = (headers) => {
  //   const expandedHeaders = [];

  //   headers.forEach((header) => {
  //     if (
  //       header.showAllDeatails &&
  //       Array.isArray(header.showAllDeatails) &&
  //       header.showAllDeatails.length > 0
  //     ) {
  //       header.showAllDeatails.forEach((detail) => {
  //         expandedHeaders.push({
  //           name: `${header.name} ${detail.name}`,
  //           keyName: detail.name,
  //           isExportShow: header.isExportShow,
  //           isShow: true,
  //         });
  //       });
  //     } else {
  //       expandedHeaders.push(header);
  //     }
  //   });

  //   return expandedHeaders;
  // };

  exportPDF = async () => {
    const {
      headers = [],
      rows = [],
      title,
      reportName,
      showTripSummary = false,
      fromDate,
      toDate,
      showcommon = false,
      reportGeneratedBy,
      innerTable = false,
      foruptimeMonthly = false, // Add this prop
      innerTableforVehicleInteractionNotesList = false,
      isGroupByTransporter = false,
      outerTableData = [],
      outerHeaders = [],
      innerHeaders = [],
      isGroupByDistanceRange = false, // Add this line
      multiHeader = false, // Add this
      nestedHeadersConfig = {}, // Add this
      hierarchyList = [], // Add this
      showRangeInHeader = false,
    } = this.props;

    console.log("Exporting PDF with data:", rows);

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // 🔹 Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    let displayTitle = title || "Report";
    if (showRangeInHeader && fromDate && toDate) {
      const fromStr = moment(fromDate).format("DD-MM-YYYY HH:mm:ss");
      const toStr = moment(toDate).format("DD-MM-YYYY HH:mm:ss");
      displayTitle = `${displayTitle} (From: ${fromStr} To: ${toStr})`;
    }
    doc.text(displayTitle, pageWidth / 2, 30, { align: "center" });

    // 🔹 Report Generated Date
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(
      `Report Generated At: ${moment().format("DD-MM-YYYY HH:mm:ss")}`,
      40,
      50,
    );

    // 🔹 Report Generated By (only if present)
    if (reportGeneratedBy) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(80);
      doc.text(`Report Generated By: ${reportGeneratedBy}`, 40, 65);
    }

    // 🔹 Logo (async load and add)
    try {
      const logoBase64 = await getBase64FromUrl(logoDlb);
      doc.addImage(logoBase64, "PNG", pageWidth - 100, 15, 60, 30);
    } catch (err) {
      console.warn("Logo load failed:", err);
    }

    let currentYPosition = 70;

    // 🔹 SEPARATE CONDITION FOR UPTIME MONTHLY - Handle this first
    if (foruptimeMonthly) {
      await this.generateUptimeMonthlyPDF(
        doc,
        rows,
        currentYPosition,
        pageWidth,
        pageHeight,
      );

      // Save and return early to avoid executing other conditions
      doc.save(
        `${reportName || "Report"}_${moment().format(
          "DD-MM-YYYY_HH-mm-ss",
        )}.pdf`,
      );
      return; // Important: return early to prevent other code execution
    }

    if (isGroupByTransporter && outerTableData.length > 0) {
      await this.generateGroupByTransporterPDF(
        doc,
        outerTableData,
        outerHeaders,
        innerHeaders,
        currentYPosition,
        pageWidth,
        pageHeight,
      );

      doc.save(
        `${reportName || "Report"}_${moment().format(
          "DD-MM-YYYY_HH-mm-ss",
        )}.pdf`,
      );
      return;
    }

    // In the exportPDF method, add this condition:
    if (isGroupByDistanceRange && outerTableData.length > 0) {
      await this.generateNitReportPDF(
        doc,
        outerTableData,
        outerHeaders,
        innerHeaders,
        currentYPosition,
        pageWidth,
        pageHeight,
      );

      doc.save(
        `${reportName || "Report"}_${moment().format(
          "DD-MM-YYYY_HH-mm-ss",
        )}.pdf`,
      );
      return;
    }

    // 🔹 ALL EXISTING CODE BELOW REMAINS EXACTLY THE SAME
    if (showcommon) {
      // Existing showcommon code...
      const firstRow = rows.length > 0 ? rows[0] : {};
      const vehicleNumber = firstRow.vehicleNumber;
      const siteName = firstRow.areaName;

      if (vehicleNumber || siteName) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(0, 58, 107);

        if (vehicleNumber) {
          doc.text(`Vehicle Number: ${vehicleNumber}`, 40, currentYPosition);
          currentYPosition += 15;
        }

        if (siteName) {
          doc.text(`Site Name: ${siteName}`, 40, currentYPosition);
          currentYPosition += 15;
        }
      }
    }

    if (fromDate && toDate && !showRangeInHeader) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 58, 107);

      const formattedFromDate = moment(fromDate).format("DD-MM-YYYY HH:mm:ss");
      const formattedToDate = moment(toDate).format("DD-MM-YYYY HH:mm:ss");

      doc.text(
        `Tracking Period: ${formattedFromDate} to ${formattedToDate}`,
        40,
        currentYPosition,
      );
      currentYPosition += 20;
    }

    if (showTripSummary && rows.length > 0) {
      // Existing showTripSummary code...
      const tripSummary = rows[0];

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(0, 58, 107);
      doc.text("Trip Summary", 40, currentYPosition);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0);

      const summaryDetails = [
        { label: "Vehicle Number:", value: tripSummary.vehicleNumber },
        { label: "Trip Duration:", value: tripSummary.duration },
        { label: "Status:", value: tripSummary.status },
        { label: "Area Name:", value: tripSummary.areaName },
        { label: "Start Time:", value: tripSummary.startTime },
        { label: "End Time:", value: tripSummary.endTime },
        { label: "Source:", value: tripSummary.source },
        { label: "Destination:", value: tripSummary.destination },
      ];

      currentYPosition += 20;
      summaryDetails.forEach((detail, index) => {
        const xPosition = index % 2 === 0 ? 40 : pageWidth / 2 + 20;
        if (index % 2 === 0 && index !== 0) currentYPosition += 15;

        doc.setFont("helvetica", "bold");
        doc.text(detail.label, xPosition, currentYPosition);
        doc.setFont("helvetica", "normal");
        doc.text(
          `: ${detail.value || "N/A"}`,
          xPosition + 70,
          currentYPosition,
        );
      });

      currentYPosition += 40;
    }
    if (innerTableforVehicleInteractionNotesList) {
      await this.generateVehicleInteractionNotesPDF(
        doc,
        rows,
        currentYPosition,
        pageWidth,
        pageHeight,
      );
    }
    if (multiHeader) {
      await this.generateTreeFlowPDF(
        doc,
        rows,
        headers,
        nestedHeadersConfig,
        currentYPosition,
        hierarchyList,
      );

      doc.save(
        `${reportName || "Report"}_${moment().format("DD-MM-YYYY_HH-mm-ss")}.pdf`,
      );
      return;
    } else if (
      innerTable &&
      rows &&
      rows.length > 0 &&
      rows[0].hasOwnProperty("number") &&
      rows[0].hasOwnProperty("alerts")
    ) {
      await this.generateInterleavedAlertPDF(
        doc,
        rows,
        currentYPosition,
        pageWidth,
        pageHeight,
      );
    } else {
      // const expandedHeaders = this.expandTransporterHeaders(headers);
      const visibleHeaders = headers.filter((h) => h?.isExportShow !== false);
      const headerRow = visibleHeaders.map((h) => h.name || h.keyName || "");

      const dataRows = showTripSummary ? rows.slice(1) : rows;

      const bodyRows = dataRows.map((row, index) =>
        visibleHeaders.map((header) => {
          let cellValue = formatCellValue(row, header, index);
          if (header?.keyName === "tripStatus" || header?.isBox) {
            cellValue = this.mapStatusValue(cellValue);
          }
          return cellValue;
        }),
      );

      const columnStyles = {};
      if (this.props.customColumnWidths) {
        visibleHeaders.forEach((header, index) => {
          const keyName = header.keyName;
          if (this.props.customColumnWidths[keyName]) {
            columnStyles[index] = {
              cellWidth: this.props.customColumnWidths[keyName],
            };
          }
        });
      }

      doc.autoTable({
        startY: currentYPosition,
        head: [headerRow],
        body: bodyRows,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 4,
          valign: "middle",
          halign: "center",
        },
        columnStyles: columnStyles, // Add custom column widths

        headStyles: {
          fillColor: [0, 58, 107],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text(
            `Page ${
              doc.internal.getCurrentPageInfo().pageNumber
            } of ${pageCount}`,
            pageWidth - 60,
            pageHeight - 20,
          );
        },
      });
    }

    doc.save(
      `${reportName || "Report"}_${moment().format("DD-MM-YYYY_HH-mm-ss")}.pdf`,
    );
  };

  // Generate interleaved structure: outer row followed by inner table for each vehicle
  generateInterleavedAlertPDF = async (
    doc,
    rows,
    startY,
    pageWidth,
    pageHeight,
  ) => {
    let currentY = startY;

    // Outer table headers for vehicle summary
    const outerHeaders = [
      "S. NO",
      "VEHICLE NUMBER",
      "LAST EVENT TIME",
      "TOTAL ALERTS",
    ];

    // Inner table headers for alert details
    const innerHeaders = [
      "S. No",
      "Alert Type",
      "Area",
      "Mine Name",
      "Vehicle Number",
      "Satellite Count",
      "Vehicle Type",
      "Vehicle Sub Type",
      "Transporter Name",
      "Power Restore Time",
      "Power Restore location",
      "Nearest Landmark",
      "Server Time",
      "Event Time",
      "Firmware Version",
      "Alert Location",
      "GeoFence Enter Time",
      "GeoFence Enter Location",
      "Duration",
      "Speed (Km)",
      "Reason",
      "Remarks",
    ];

    // Define table widths - outer table wider, inner table smaller
    const outerTableWidth = pageWidth - 80;
    const innerTableWidth = pageWidth - 80; // Smaller than outer table

    // Generate content for each vehicle one by one
    for (let i = 0; i < rows.length; i++) {
      const vehicle = rows[i];

      // Check if we need a new page before adding outer table row
      if (currentY > pageHeight - 150) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Outer Table Row for this vehicle
      const outerTableData = [
        [
          (i + 1).toString(),
          vehicle.number || "N/A",
          vehicle?.alerts[0]?.createdAt
            ? moment(vehicle?.alerts[0]?.createdAt).format("DD-MM-YYYY HH:mm")
            : "N/A" || "N/A",
          vehicle.alerts ? vehicle?.alerts?.length.toString() : "0",
        ],
      ];

      // Generate single row outer table
      doc.autoTable({
        startY: currentY,
        head: [outerHeaders],
        body: outerTableData,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 6,
          valign: "middle",
          halign: "center",
        },
        headStyles: {
          fillColor: [0, 58, 107], // Navy Blue
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: 0,
          fontStyle: "bold",
        },
        margin: { top: 5, bottom: 0 }, // Remove bottom margin to reduce space
        tableWidth: outerTableWidth,
      });

      // Set currentY directly after outer table with minimal gap
      currentY = doc.lastAutoTable.finalY + 2; // Reduced from 10 to 2

      // Check if we need a new page before adding inner table
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 40;
      }

      // 🔹 Inner Table for this vehicle's alerts
      if (vehicle.alerts && vehicle.alerts.length > 0) {
        // Prepare inner table data according to innerHeaders order
        const innerTableData = vehicle.alerts.map((alert, alertIndex) => [
          (alertIndex + 1).toString(), // S. No
          alert?.alertType === "POWER_CUT"
            ? "DISCONNECTION_ALERT"
            : alert?.alertType || "N/A",
          this.getNestedValue(alert, "vehicle.site.name") || "N/A",
          this.getNestedValue(alert, "vehicle.mineName") || "N/A",
          this.getNestedValue(alert, "vehicle.number") || "N/A",
          alert?.sateliteCount ? alert?.sateliteCount : "---",
          this.getNestedValue(alert, "vehicle.vehicleActualTypeDTO.name") ||
            "N/A",
          this.getNestedValue(alert, "vehicle.vehicleType.name") || "N/A",
          this.getNestedValue(alert, "vehicle.vendor.name") || "N/A",
          alert.powercutRestoreTime
            ? moment(alert.powercutRestoreTime).format("DD-MM-YYYY HH:mm:ss")
            : "N/A",
          alert?.powercutRestoreLatitude // GeoFence Enter Location
            ? `${alert?.powercutRestoreLatitude || "--"} , ${
                alert?.powercutRestoreLongitude || "--"
              }`
            : "N/A", //  "Power Restore location",
          this.getNestedValue(alert, "nearestLandmark") || "N/A", // "Nearest Landmark",

          alert?.serverTime // Server Time
            ? moment(alert.serverTime).format("DD-MM-YYYY HH:mm:ss")
            : "N/A",
          alert.createdAt // Event Time
            ? moment(alert.createdAt).format("DD-MM-YYYY HH:mm:ss")
            : "N/A",
          this.getNestedValue(alert, "firmwareVersion") || "N/A", // Firmware Version
          this.formatLocation(alert) || "N/A", // Alert Location
          alert?.geofenceEnterTime // GeoFence Enter Time
            ? moment(alert.geofenceEnterTime).format("DD-MM-YYYY HH:mm:ss")
            : "N/A",
          alert?.geofenceEnterLatitude // GeoFence Enter Location
            ? `${alert?.geofenceEnterLatitude || "--"} , ${
                alert?.geofenceEnterLongitude || "--"
              }`
            : "N/A",
          alert?.geofenceEnterTime // Duration
            ? this.getDuration(alert?.createdAt, alert?.geofenceEnterTime) ||
              "N/A"
            : "N/A",
          alert?.speed ? alert?.speed?.toFixed(2) : "N/A", // Speed (Km)
          alert?.reason ? alert?.reason.name : "N/A",
          alert?.remarks ? alert?.remarks : "N/A",
        ]);

        // Generate inner table with smaller width
        doc.autoTable({
          startY: currentY,
          head: [innerHeaders],
          body: innerTableData,
          theme: "grid",
          styles: {
            fontSize: 7,
            cellPadding: 3,
            valign: "middle",
            halign: "left",
          },
          headStyles: {
            fillColor: [70, 130, 180], // Steel Blue for inner table
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          tableWidth: innerTableWidth, // Smaller width
          pageBreak: "auto",
          didDrawPage: (data) => {
            // Footer with page numbers
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(
              `Page ${
                doc.internal.getCurrentPageInfo().pageNumber
              } of ${pageCount}`,
              pageWidth - 60,
              pageHeight - 20,
            );
          },
        });

        currentY = doc.lastAutoTable.finalY + 15; // Reduced spacing
      } else {
        // No alerts for this vehicle
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("No alerts found for this vehicle", 50, currentY);
        currentY += 20;
      }

      // Add separation between vehicles (except for the last one)
      if (i < rows.length - 1) {
        // Add a subtle separator line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(40, currentY, pageWidth - 40, currentY);
        currentY += 10; // Reduced spacing
      }
    }
  };

  render() {
    return (
      <div id="exportPdfButton" onClick={this.exportPDF}>
        <PictureAsPdfIcon
          style={{ cursor: "pointer", color: "red", fontSize: 28 }}
        />
      </div>
    );
  }
}

export default ExportPdf;
